import { useCompanyApi } from '@/api/client/api-client';
import { HttpMethod } from '@/api/client/http';
import { notifyError } from '@/bugsnag';
import type { OnFetchErrorContext, UseFetchOptions } from '@vueuse/core';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';

export const performStoreAction = <Response = void, Payload = MaybeRefOrGetter>(
    url: string | ComputedRef<string>,
    method: HttpMethod,
    payload?: Payload,
    options?: UseFetchOptions,
) => {
    if (!url || !method) {
        throw new Error('URL and HTTP method are required');
    }

    const companyFetch = useCompanyApi({ auth: 'jwt' });
    const fetch = companyFetch(url, {
        onFetchError(ctx: OnFetchErrorContext) {
            /**
             * Intercept and set the error to `data` because
             * error details and messages are located in `ctx.data`.
             */
            ctx.error = structuredClone(ctx.data);
            notifyError(new Error('performStoreAction failed'), 'performStoreAction', {
                url,
                method,
                payload,
                options,
                data: ctx.data,
            });
            return ctx;
        },
        ...options,
    });

    switch (method) {
        case HttpMethod.GET:
            return fetch.json<Response>();
        case HttpMethod.POST:
            return fetch.post(payload).json<Response>();
        case HttpMethod.PUT:
            return fetch.put(payload).json<Response>();
        case HttpMethod.DELETE:
            return fetch.delete().json<Response>();
        case HttpMethod.PATCH:
            return fetch.patch(payload).json<Response>();
        default:
            throw new Error(`Unsupported HTTP method: ${method}`);
    }
};
